/**
 * Cache age indicator component (Development only)
 * Shows cache age and provides manual refresh option
 */

import { useEffect, useState } from 'react';

interface CacheIndicatorProps {
  cacheAge: number; // in seconds
  onRefresh: () => void;
  label?: string;
}

export function CacheIndicator({ cacheAge, onRefresh, label = 'Cache' }: CacheIndicatorProps) {
  const [displayAge, setDisplayAge] = useState(cacheAge);

  // Update display age every second
  useEffect(() => {
    setDisplayAge(cacheAge);
    const interval = setInterval(() => {
      setDisplayAge((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [cacheAge]);

  // Only show in development
  if (!import.meta.env.DEV) return null;

  const formatAge = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h`;
  };

  const getAgeColor = (seconds: number): string => {
    if (seconds < 60) return 'text-green-600';
    if (seconds < 300) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="fixed top-4 right-4 bg-white rounded-lg shadow-md border border-gray-200 px-4 py-2 flex items-center gap-3 z-50">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">📦 {label}:</span>
        <span className={`text-sm font-semibold ${getAgeColor(displayAge)}`}>
          {formatAge(displayAge)} old
        </span>
      </div>
      <button
        onClick={onRefresh}
        className="text-blue-600 hover:text-blue-800 transition-colors text-sm font-medium"
        title="Refresh data"
      >
        ↻ Refresh
      </button>
    </div>
  );
}
